import type { CSSProperties } from 'react'
import { FileText, Search } from 'lucide-react'
import { blockTheme, hsl } from '@teach/ui'
import { useArticlePickerList } from '@/features/instructions/hooks/useArticlePickerList'
import { BrandLoader } from '@/components/common/BrandLoader'
import {
  ArticlesEmpty,
  CreateArticleButton,
} from '@/features/instructions/components/ArticlesEmpty'
import type { Instruction } from '@/features/instructions/types'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/**
 * Выбор статьи прямо на экране урока (пустой блок статьи): тот же список, что в
 * панели справа, в светлых цветах холста. Клик пишет статью в черновик блока, и
 * автосохранение панели отправляет `PUT` этого блока. Без `onPick` (блок ещё
 * создаётся на сервере) строки погашены — выбор ушёл бы во временную запись.
 */
export const CanvasArticleChooser = ({
  onPick,
}: {
  onPick?: (articleId: number, article: Instruction) => void
}) => {
  const m = useMessages(messages)
  const list = useArticlePickerList()
  const muted = hsl(blockTheme.foreground, 0.55)
  return (
    <div className="flex h-full w-full flex-col gap-3">
      <p className="text-center text-2xl font-bold" style={{ color: hsl(blockTheme.foreground) }}>
        {m.chooseArticle}
      </p>
      {/* Стеклянное поле поиска: узор урока просвечивает и размывается, фокус — акцент. */}
      <label
        className="flex h-11 items-center gap-2 rounded-xl border-[1.5px] border-[var(--sb-border)] bg-[var(--sb-bg)] px-3 shadow-sm backdrop-blur-xl backdrop-saturate-150 transition-colors focus-within:border-[var(--sb-focus)]"
        style={
          {
            '--sb-bg': hsl(blockTheme.background, 0.5),
            '--sb-border': hsl(blockTheme.background, 0.8),
            '--sb-focus': hsl(blockTheme.primary),
          } as CSSProperties
        }
      >
        <Search className="size-4 shrink-0" style={{ color: hsl(blockTheme.quizMuted) }} />
        <input
          value={list.searchInput}
          onChange={(e) => list.setSearchInput(e.target.value)}
          placeholder={m.searchArticle}
          className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--sb-ph)]"
          style={
            {
              color: hsl(blockTheme.quizText),
              '--sb-ph': hsl(blockTheme.quizMuted),
            } as CSSProperties
          }
        />
      </label>
      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto">
        {list.isLoading ? (
          <div className="flex justify-center py-6">
            <BrandLoader className="size-8" />
          </div>
        ) : list.isError ? (
          <p className="py-6 text-center text-sm" style={{ color: muted }}>
            {m.articlesLoadFailed}
          </p>
        ) : list.items.length === 0 && !list.debouncedQ ? (
          // По центру свободной части экрана, а не прижат к поиску.
          <div className="flex h-full items-center justify-center">
            <ArticlesEmpty />
          </div>
        ) : list.items.length === 0 ? (
          <p className="py-6 text-center text-sm" style={{ color: muted }}>
            {m.nothingFound}
          </p>
        ) : (
          list.items.map((article) => (
            <button
              key={article.id}
              type="button"
              disabled={!onPick}
              onClick={() => onPick?.(article.id, article)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-lg font-medium transition-colors hover:bg-[var(--row-hover)] disabled:opacity-60"
              style={{
                color: hsl(blockTheme.foreground),
                ['--row-hover' as string]: hsl(blockTheme.primary, 0.1),
              }}
            >
              <FileText className="size-6 shrink-0" style={{ color: hsl(blockTheme.primary) }} />
              <span className="truncate">{article.title}</span>
            </button>
          ))
        )}
      </div>
      {list.items.length > 0 ? <CreateArticleButton className="w-full" /> : null}
    </div>
  )
}
